/**
 * Pure helpers used by the map widget — aggregating raw points into the
 * stats shown in the overlay panel + emitted to listeners via the
 * `conny-map-stats` CustomEvent.
 *
 * Pure / no React / no DOM → trivially unit-testable.
 */
import type { MapBounds, MapCasePoint } from "../../../shared/api";
import { MAX_RADIUS, MIN_RADIUS } from "./constants";

/**
 * What's currently driving the visible subset of points and the stats /
 * title shown on top. The widget computes one of these on every render and
 * derives both the filtered list and the title label from it.
 */
export type MapScope =
  | { kind: "zip"; zip: string }
  | { kind: "area"; lat: number; lng: number; radiusKm: number }
  | { kind: "bounds"; bounds: MapBounds }
  | { kind: "city"; city: string };

export interface MapStats {
  /** Number of buildings (data points) shown. */
  houses: number;
  /** Sum of `cases` across all points. */
  cases: number;
  /** Sum of `cws` (cases-with-savings) across all points. */
  cws: number;
  /** Sum of `closed` across all points. */
  closed: number;
  /** Sum of `open` across all points. */
  open: number;
  /** Sum of recorded monthly savings (€/month). */
  total: number;
}

export function computeStats(points: MapCasePoint[]): MapStats {
  const acc: MapStats = {
    houses: points.length,
    cases: 0,
    cws: 0,
    closed: 0,
    open: 0,
    total: 0,
  };
  for (const p of points) {
    acc.cases += p.cases;
    acc.cws += p.cws;
    acc.closed += p.closed;
    acc.open += p.open;
    acc.total += p.total;
  }
  return acc;
}

/**
 * Marker radius scaling. Mirrors the formula from the original
 * berlinmap.html (`Math.sqrt(eur)*0.35` etc.) so existing visual reviews
 * carry over.
 */
export function markerRadius(eurTotal: number, cases: number): number {
  if (eurTotal > 0) {
    return Math.max(
      MIN_RADIUS,
      Math.min(MAX_RADIUS, MIN_RADIUS + Math.sqrt(eurTotal) * 0.35),
    );
  }
  return Math.max(
    MIN_RADIUS,
    Math.min(10, MIN_RADIUS + Math.sqrt(cases) * 1.2),
  );
}

/**
 * Zoom-dependent dot styling. At city-wide zoom thousands of dots must read
 * as *texture* (small, translucent); at street zoom each dot is a tappable
 * subject (full size, solid). Linear ramp between the two anchors.
 */
export const DOT_ZOOM_MIN = 11; // city overview
export const DOT_ZOOM_MAX = 16; // street level
// Zoom at which the cluster mode stops grouping and shows individual dots.
// Deliberately well below DOT_ZOOM_MAX so detail appears without having to
// zoom all the way to street level.
export const CLUSTER_DISABLE_ZOOM = 13;

export function dotStyleForZoom(zoom: number): {
  radius: number;
  fillOpacity: number;
} {
  const t = Math.min(
    1,
    Math.max(0, (zoom - DOT_ZOOM_MIN) / (DOT_ZOOM_MAX - DOT_ZOOM_MIN)),
  );
  return {
    radius: Math.round((MIN_RADIUS + t * 5) * 10) / 10,
    fillOpacity: Math.round((0.45 + t * 0.35) * 100) / 100,
  };
}

/**
 * Cluster bubble content + sizing. The label is the summed `cases` of the
 * clustered buildings (doubles as social proof, so it's formatted with the
 * locale's thousands separator); the tier drives CSS size/prominence.
 * 36px floor keeps the smallest bubble a comfortable tap target.
 */
export function clusterBadge(
  totalCases: number,
  locale: "en" | "de",
): { label: string; tier: "sm" | "md" | "lg"; px: number } {
  const tier = totalCases < 100 ? "sm" : totalCases < 1000 ? "md" : "lg";
  const px = tier === "sm" ? 36 : tier === "md" ? 44 : 52;
  const label = new Intl.NumberFormat(
    locale === "de" ? "de-DE" : "en-US",
  ).format(totalCases);
  return { label, tier, px };
}

/** Top entry (by case count) of a `landlords` map, or null if empty. */
export function topLandlord(
  landlords: Record<string, number>,
): [string, number] | null {
  const entries = Object.entries(landlords);
  if (!entries.length) return null;
  entries.sort((a, b) => b[1] - a[1]);
  return entries[0]!;
}

/** Great-circle distance (km) — duplicated locally to keep this file pure. */
function haversineKm(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
): number {
  const R = 6371;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

/** Filter a list of points down to those that match the active scope. */
export function pointsInScope(
  points: MapCasePoint[],
  scope: MapScope,
): MapCasePoint[] {
  switch (scope.kind) {
    case "zip":
      return points.filter((p) => p.zip_code === scope.zip);
    case "area":
      return points.filter(
        (p) =>
          haversineKm(scope.lat, scope.lng, p.lat, p.lng) <= scope.radiusKm,
      );
    case "bounds": {
      const { west, south, east, north } = scope.bounds;
      return points.filter(
        (p) =>
          p.lat >= south && p.lat <= north && p.lng >= west && p.lng <= east,
      );
    }
    case "city":
    default:
      return points;
  }
}

/** i18n key + interpolation vars used by the stats panel title. */
export function scopeTitle(scope: MapScope): {
  key: string;
  vars: Record<string, string | number>;
} {
  switch (scope.kind) {
    case "zip":
      return { key: "map.titleZip", vars: { zip: scope.zip } };
    case "area":
      return { key: "map.titleArea", vars: {} };
    case "bounds":
      return { key: "map.titleView", vars: {} };
    case "city":
    default: {
      const city = scope.kind === "city" ? scope.city : "";
      const label = city ? city.charAt(0).toUpperCase() + city.slice(1) : "";
      return { key: "map.titleCity", vars: { city: label } };
    }
  }
}
